import { ReactElement } from 'react';
import styled, { AnyStyledComponent } from 'styled-components';
import PropTypes from 'prop-types';
import { Icon, iconPropType } from '../Icon';
import { IconType } from '../types';

const Wrapper = styled.li<{ isActive: boolean }>`
  letter-spacing: 0.02em;
  font-size: 1rem;
  fill: ${p => p.theme.colors.primary};
  color: ${p => p.theme.colors.text.primary};
  max-height: 2.625em;
  border-bottom: 1px solid rgb(209 213 216 / 32%);
  ${({ isActive, theme }) =>
    isActive &&
    `
  background-color: ${theme.colors.aux.lightOrange};
  text-decoration: none;
  font-family: Frutiger-Bold;
  border-left: 0.25em solid ${theme.colors.primary};
  `}

  ${({ isActive, theme }) =>
    !isActive &&
    `
&&: hover {
    opacity: 0.96;
    background-color: ${theme.colors.primary};
    fill: ${theme.colors.aux.white};
    color: ${theme.colors.aux.white};
    box-shadow: inset 0 0 0 4px ${theme.colors.aux.white};
    outline: ${theme.colors.primary} solid;
    outline-offset: -2px;
  }
`}
`;

const Link = styled.a`
  display: flex;
  justify-content: space-between;
  padding: 0.3em 0.3em 0 0;
  border-left: 0.25em solid transparent;
  font-family: inherit;
  fill: inherit;
  color: inherit;
  text-decoration: none;
  &&: hover {
    color: inherit;
    text-decoration: none;
  }
  &&: focus {
    color: inherit;
    text-decoration: none;
  }
`;

const LabelWrapper = styled.div`
  flex-grow: 1;
  margin: 0.3em;
  font-family: inherit;
  color: inherit;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
`;

const ColoredIcon = styled(Icon).attrs({
  height: '1.6em',
  width: '1.6em',
  tabIndex: -1,
})`
  fill: inherit;
  margin: 0.35em 0.35em 0.35em 0.75em;
  &&: focus {
    outline: none;
  }
`;

// important style added below to fix safari issue
const NewWindowIcon = styled(Icon).attrs({
  height: '.8em',
  width: '.8em',
  tabIndex: -1,
})`
  align-self: center;
  overflow: visible !important;
  &&: focus {
    outline: none;
  }
`;

interface NavigationLinkProps {
  label: string;
  linkComponent?: AnyStyledComponent | ReactElement | Function;
  linkProps?: { [key: string]: string | ((...args: any[]) => void) };
  icon?: IconType;
  key?: string | number;
  openInNewTab?: boolean;
  isActive?: boolean;
}

function NavigationLink({
  icon,
  label,
  key,
  linkComponent = styled.a``,
  linkProps,
  openInNewTab = false,
  isActive = false,
}: NavigationLinkProps) {
  return (
    <>
      <Wrapper key={key} isActive={isActive}>
        <Link as={linkComponent as any} {...linkProps}>
          {icon && <ColoredIcon icon={icon} />}
          <LabelWrapper>
            {label}
            {openInNewTab && (
              <NewWindowIcon
                alt="Opens window in a new tab"
                title="Opens window in a new tab"
                icon="NewWindow"
              />
            )}
          </LabelWrapper>
        </Link>
      </Wrapper>
    </>
  );
}

NavigationLink.propTypes = {
  /**
   * The text on the NavigationLink
   */
  label: PropTypes.string.isRequired,
  /**
   * The Icon that will be displayed on the left, must be an icon in our library
   */
  icon: iconPropType,
  /**
   * Unique key to prevent warnings about list items missing keys
   */
  key: PropTypes.string,
  /**
   * Determines weather to apply isActiveClass class on the element
   */
  isActive: PropTypes.bool,
  /**
   *  If set to true will launch href in a new tab. Will also provide indicator on UI to let user know this is an external link
   */
  openInNewTab: PropTypes.bool,
  /**
   * The element to be used that is rendered as the link. If you are using react router, likely a Link component reference. If this is omitted, a basic <a> tag will be used for links
   */
  linkComponent: PropTypes.any,
  /**
   * The props for the link component that are spread on
   */
  linkProps: PropTypes.object,
};

export default NavigationLink;
